/* iter-training-v-ach-coach — Live-coach hook for the editor.
 *
 * Watches `chapter.content` — every time it grows by ~500 characters
 * (a rough proxy for a paragraph or two of new prose) it debounces
 * for ~2.5 s and then fires POST /coach/chapter-analyze. Also exposes
 * a manual `trigger()` for a toolbar button. Auto-firing is opt-in
 * per user via localStorage (`pw_live_coach_auto=1` by default).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const AUTO_KEY = 'pw_live_coach_auto';
const AUTO_THRESHOLD_CHARS = 500;   // ~80-100 words between auto-fires
const AUTO_DEBOUNCE_MS = 2500;

function _autoEnabled() {
  try { return window.localStorage.getItem(AUTO_KEY) === '1'; }
  catch { return false; }
}

export function setLiveCoachAuto(on) {
  try { window.localStorage.setItem(AUTO_KEY, on ? '1' : '0'); }
  catch { /* ignore */ }
}

export function isLiveCoachAuto() { return _autoEnabled(); }

export default function useLiveCoach({ projectId, chapterId, content }) {
  const [suggestion, setSuggestion] = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);
  const lastLenRef = useRef(0);
  const timerRef   = useRef(null);
  const inflight   = useRef(false);

  const analyze = useCallback(async (text) => {
    if (!projectId) return;
    if (inflight.current) return;
    inflight.current = true;
    setLoading(true);
    setError(null);
    try {
      const r = await axios.post(`${API}/coach/chapter-analyze`, {
        project_id: projectId,
        chapter_id: chapterId || '',
        text:       (text || '').slice(0, 40000),
      });
      setSuggestion(r.data);
      lastLenRef.current = (text || '').length;
    } catch (e) {
      setError(e?.response?.data?.detail || 'Coach unavailable.');
    } finally {
      inflight.current = false;
      setLoading(false);
    }
  }, [projectId, chapterId]);

  // Manual "Suggest next beat" trigger.
  const trigger = useCallback(() => analyze(content), [analyze, content]);

  // Auto-fire on growth.
  useEffect(() => {
    if (!_autoEnabled()) return undefined;
    if (!projectId || !chapterId) return undefined;
    const len = (content || '').length;
    if (len < AUTO_THRESHOLD_CHARS) return undefined;
    const grew = len - lastLenRef.current;
    if (grew < AUTO_THRESHOLD_CHARS) return undefined;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => analyze(content), AUTO_DEBOUNCE_MS);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [content, projectId, chapterId, analyze]);

  // Reset stored length when the chapter switches.
  useEffect(() => {
    lastLenRef.current = 0;
    setSuggestion(null);
  }, [chapterId]);

  const dismiss = useCallback(() => setSuggestion(null), []);

  return { suggestion, loading, error, trigger, dismiss };
}
